import React, { useState, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataReactTable from "../DataTable/components/DataReactTable";
import VehicleComponent from "../../shared/components/table/components/VehicleComponent";
import {
  AddFormButton,
  ColumnHeading,
} from "../../shared/components/TableElements";
import { NATEXT } from "../../shared/constants";
import getUnixTime from "date-fns/getUnixTime";
import { Link } from "react-router-dom";
import {
  formatNumber,
  getDistanceUnit,
  getListingDisplayDate,
  getTranslatedFuelTypeUnit,
  getTranslatedTransactionType,
  getVolumeUnit,
} from "../../shared/helpers";
import ExplorePopup from "../../shared/components/ExplorePopup";
import { useAuth } from "../../contexts/AuthContext";
import { LinkSpan } from "../../shared/components/form/FormElements";
import { useLocation } from "react-router";
import useFetchData from "../../shared/custom-hooks/useFetchData";
import getDefaultFilterData from "../../shared/constants/getDefaultFilterData";

const FillupsTable = () => {
  const { t } = useTranslation("common");
  const { isViewOnly } = useAuth();

  const location = useLocation();
  //const fromCancel = location.state?.fromCancel;
  //const [fromCancel, setFromCancel] = useState(fromCancelBtn);
  //console.log(fromCancel);

  const { data, values, status } = useSelector((state) => state.filter);
  const {
    draw,
    length,
    search,
    order,
    start_date,
    end_date,
    start,
    filter_group_id,
    filter_vehicle_id,
    querystring,
  } = data;
  //let { start_date, end_date, ...otherData } = data;
  const columns = useSelector((state) => state.advancedFilter.fillups);
  const newData = { ...data, ...columns };
  const rowData = useSelector((state) => state.filter.rowData);
  const optionValues = useSelector((state) => state.filter.values);
  const { minDate, maxDate, noOfRecords } = useSelector(
    (state) => state.columns
  );
  const { pathname } = location;
  const { currency, consumption, distance, volume, hourlyConsumption } =
    useSelector((state) => state.units);

  const users = useSelector((state) => state.users.list);
  const { orgDate } = useSelector((state) => state.user);

  //Logic for first time load(Reload start_date and end_date)
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);

  const [showExplorePopup, setShowExplorePopup] = useState(false);

  useFetchData({
    startDate: 0,
    endDate: getUnixTime(new Date()),
    query: "get_all_fillups", //"getallfillups"
    sortIndex: 2,
  });

  //KPI Values
  let fillupsValuesArray = [
    {
      id: 0,
      key: "footertotalcost",
      name: t("cost"),
      value: optionValues?.footertotalcost
        ? `${formatNumber(optionValues?.footertotalcost)} ${currency}`
        : NATEXT,
      // units: currency,
      // icon: "currency",
    },
    {
      id: 1,
      key: "footertotalqty",
      name: t("qty_tv"),
      value: optionValues?.footertotalqty
        ? `${formatNumber(optionValues.footertotalqty)} ${getVolumeUnit(
            volume
          )}`
        : NATEXT,
      // units: volume,
      // icon: "gasStation",
    },
    {
      id: 2,
      key: "footertotaleff",
      name: t("avg_eff"),
      // value:
      //   optionValues?.footertotaleff && consumption !== "L/100km"
      //     ? `${+optionValues.footertotaleff?.toFixed(2)} ${consumption}`
      //     : optionValues?.footertotaleff && consumption === "L/100km"
      //     ? `${(100 / +optionValues.footertotaleff)?.toFixed(2)} ${consumption}`
      //     : NATEXT,
      value: optionValues?.footertotaleff
        ? `${formatNumber(optionValues.footertotaleff)} ${consumption}`
        : NATEXT,
      // units: consumption,
      // icon: "gauge",
    },
    // {
    //   id: 3,
    //   key: "footerhourlyeff",
    //   name: t("hourly_eff"),
    //   value: optionValues?.footerhourlyeff
    //     ? `${+optionValues.footerhourlyeff?.toFixed(2)} ${consumption}`
    //     : NATEXT,
    // },
  ];

  if (optionValues?.footerhourlyeff) {
    fillupsValuesArray.push({
      id: 3,
      key: "footerhourlyeff",
      name: t("hourly_eff"),
      value: optionValues?.footerhourlyeff
        ? `${formatNumber(optionValues.footerhourlyeff)} ${hourlyConsumption}`
        : NATEXT,
    });
  }

  const {
    defaultContainsData,
    defaultRangeData,
    defaultTransactionTypeData,
    defaultFuelTypeData,
  } = getDefaultFilterData();

  const initialData = useMemo(
    () => ({
      date: {
        column: "date",
        name: t("date_tv"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
      },
      odo: {
        column: "odo",
        name: t("odo"),
        ...defaultRangeData,
      },
      distance: {
        column: "distance",
        name: t("distance"),
        ...defaultRangeData,
      },
      quantity: {
        column: "quantity",
        name: t("qty_tv"),
        ...defaultRangeData,
      },
      totalCost: {
        column: "totalCost",
        name: t("tc_tv"),
        ...defaultRangeData,
      },
      efficiency: {
        column: "efficiency",
        name: t("efficiency"),
        ...defaultRangeData,
      },
      fillingStation: {
        column: "fillingStation",
        name: t("fs_tv"),
        ...defaultContainsData,
      },
      transactionType: {
        column: "transactionType",
        name: t("transaction_type"),
        ...defaultTransactionTypeData,
        fuelCard: { header: t("fuel_card_const"), value: false },
      },
      cardNumber: {
        column: "cardNumber",
        name: t("card_number"),
        ...defaultContainsData,
      },
      fuelBrand: {
        column: "fuelBrand",
        name: t("fb_tv"),
        ...defaultContainsData,
      },
      fuelType: {
        column: "fuelType",
        name: t("fuel_type"),
        ...defaultFuelTypeData,
      },
      comments: {
        column: "comments",
        name: t("notes_tv"),
        ...defaultContainsData,
      },
    }),
    [minDate]
  );

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("vehicle"),
        accessor: "name",
        // Footer: 'Middle age:',
        // disableGlobalFilter: false,
        //width: getColumnWidth("name", "Vehicle"),
        disableFilters: true,
        Cell: ({ row }) => (
          <VehicleComponent
            vehicleName={row.original.name}
            groupName={row.original.group_name}
            vehicleId={row.original.veh_id}
            vehicleImg={row.original.veh_img}
            orgId={row.original.org_id}
          />
        ),
      },
      {
        id: 2,
        Header: t("date_tv"),
        accessor: "fillup_date",
        disableCol: true,
        //disableFilters: true,
        //width: getColumnWidth("fillup_date", "Date"),
        Cell: ({ value }) => (value ? getListingDisplayDate(value) : NATEXT),
      },
      {
        id: 3,
        Header: t("odo"),
        accessor: "odo",
        //disableFilters: true,
        //width: getColumnWidth("odo", "Odo"),
        // disableGlobalFilter: false,
        Cell: ({ row }) => {
          const { odo, primary_meter } = row?.original;
          const odoUnit =
            primary_meter === "Hours"
              ? getDistanceUnit(primary_meter)
              : getDistanceUnit(distance);
          return odo ? `${formatNumber(odo)} ${odoUnit}` : NATEXT;
        },
      },
      {
        id: 4,
        Header: t("distance"),
        accessor: "distance",
        Cell: ({ row }) => {
          const { m_fill, primary_meter } = row?.original;
          const rowDist = row?.original?.distance; //To avoid naming conflict
          const distanceUnit =
            primary_meter === "Hours"
              ? getDistanceUnit(primary_meter)
              : getDistanceUnit(distance);
          let distanceValue = rowDist
            ? `${formatNumber(rowDist)} ${distanceUnit}`
            : NATEXT;
          if (m_fill) {
            distanceValue = t("missed_fillup");
          }

          return distanceValue;
        },

        //disableFilters: true,
        //width: getColumnWidth("distance", "Distance"),
        // disableGlobalFilter: false,
      },
      {
        id: 5,
        Header: t("qty_tv"),
        accessor: "qty",
        //disableFilters: true,
        //width: getColumnWidth("qty", "Quantity"),
        // disableGlobalFilter: false,
        Cell: ({ row }) => {
          const { qty, qty_unit, p_fill } = row.original;
          let qtyString = `${formatNumber(row.original.qty)} ${getVolume(
            row.original.qty_unit
          )}`;
          if (!qty) {
            return NATEXT;
          }
          if (p_fill) {
            qtyString += ` (P)`;
          }
          return qtyString;
        },
      },
      {
        id: 6,
        Header: t("tc_tv"),
        accessor: "total_cost",
        // disableGlobalFilter: false,
        //disableFilters: true,
        //width: getColumnWidth("total_cost", "Total Cost"),
        // Cell: ({ value }) =>
        //   value ? `${+value?.toFixed(2)} ${currency}` : NATEXT,
        Cell: ({ value }) => {
          //console.log(value);
          let numberedValue = +value;
          if (numberedValue) {
            return `${formatNumber(numberedValue, 2)} ${currency}`;
          } else {
            return NATEXT;
          }
        },
      },
      {
        id: 7,
        Header: t("efficiency"),
        accessor: "eff",
        //disableFilters: true,
        //width: columnWidth,
        // disableGlobalFilter: false,
        Cell: ({ row }) => {
          const { consumption, eff, primary_meter } = row?.original;
          let efficiency = eff ? `${eff} ${consumption}` : NATEXT;
          if (consumption == "L/100km" && eff && primary_meter != "Hours") {
            efficiency = `${(100 / eff)?.toFixed(2)} ${consumption}`;
          }
          if (primary_meter === "Hours") {
            efficiency = `${eff} ${hourlyConsumption}`;
          }
          return eff ? efficiency : NATEXT;
        },
      },
      {
        id: 8,
        Header: t("filled_by"),
        accessor: "uname",
        disableFilters: true,
        //Cell: ({ value }) => users?.find((u) => u.user_id === value)?.label,
        //width: columnWidth,
        // disableGlobalFilter: false,
      },
      {
        id: 9,
        Header: t("fs_tv"),
        accessor: "filling_station",
        accessorId: "task_name",
        Cell: ({ value }) => (value ? `${value}` : NATEXT),
        //disableFilters: true,
        //width: 150,
        // disableGlobalFilter: false,
      },
      {
        id: 10,
        Header: t("transaction_type"),
        accessor: "transaction_type",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? getTranslatedTransactionType(value, t) : NATEXT,
        //disableFilters: true,
        //width: 150,
      },
      {
        id: 11,
        Header: t("card_number"),
        accessor: "card_number",
        disableSortBy: true,
        Cell: ({ value }) => (+value ? +value : NATEXT),
        //disableFilters: true,
        //width: columnWidth,
      },
      {
        id: 12,
        Header: t("fb_tv"),
        accessor: "fuel_brand",
        Cell: ({ value }) => (value ? value : NATEXT),
        //disableFilters: true,
        //width: columnWidth,
      },
      {
        id: 13,
        Header: t("fuel_type"),
        accessor: "fuel_type",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? getTranslatedFuelTypeUnit(value, t) : NATEXT,
        //disableFilters: true,
        //width: columnWidth,
      },
      {
        id: 14,
        Header: t("notes_tv"),
        accessor: "comments",
        accessorId: "comments",
        Cell: ({ value }) => (value ? value : NATEXT),
        //disableFilters: true,
        //width: columnWidth,
      },
      // {
      //   id: 14,
      //   Header: "Group Name",
      //   accessor: "group_name",
      //   //disableFilters: true,
      //   width: columnWidth,
      // },
      // {
      //   id: 'edit',
      //   Header: "",
      //   accessor: "edit",
      //   accessorId: "edit",
      //   Cell: () => <RowOptions />,
      //   disableSortBy: true,
      // },
    ],
    [rowData, distance, currency, consumption]
  );

  let count = 0;
  let defaultColumns = {};
  columnHeaders.forEach((column) => {
    defaultColumns[count] = {
      accessor: column.Header,
      header: column.Header,
    };
    count++;
  });

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };

  return (
    <>
      {showExplorePopup && (
        <ExplorePopup
          showPopup={showExplorePopup}
          setShowPopup={setShowExplorePopup}
          exploreLink={"https://embed.getsmartcue.com/IDIGFZHN"}
        />
      )}
      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around p-0 m-0">
          {/* <Col>
            <ColumnHeading>{t("fillups")}</ColumnHeading>
          </Col> */}
          <Col className="d-flex align-items-center gap-2">
            <ColumnHeading>{t("fillups")}</ColumnHeading>
            {/* <FilterButton onClick={() => setShowExplorePopup(true)}>
              <i
                className="fi fi-rr-globe"
                style={{ margin: "4px 4px 0 0" }}
              ></i>
              <span>{t("explore")}</span>
            </FilterButton> */}
            <LinkSpan
              onClick={() => setShowExplorePopup(true)}
              style={{ alignSelf: "end" }}
              data-testid="fillup-table-explore-link"
            >
              {t("explore")}
            </LinkSpan>
          </Col>
          <Col className="d-flex justify-content-end mb-1">
            {!isViewOnly && (
              <Link to={"/fillups/addnew"} data-testid="fillup-table-add-link">
                <AddFormButton
                  id="add-fillup-button"
                  data-testid="fillup-table-add-btn"
                >
                  {t("add_fill_up")}
                </AddFormButton>
              </Link>
            )}
          </Col>
        </Row>
        {/* <Row> */}
        {/* <Col>  */}
        {/* <div style={{ height: "100%" }}> */}
        <DataReactTable
          reactTableData={reactTableData}
          valuesArray={fillupsValuesArray}
          initialData={initialData}
          defaultColumns={defaultColumns}
        />
        {/* </div> */}
        {/* </Col> */}
        {/* </Row> */}
      </Container>
    </>
    // <div style={{ height: '90%', background: 'lightblue' }}>Hi</div>
  );
};

export default FillupsTable;

export const getVolume = (value) => {
  const { t } = useTranslation("common");

  if (value === "Liters") {
    return t("ltr_const");
  } else if (value === "Gallons (UK)") {
    return t("gal_uk_const");
  } else if (value === "Gallons (US)") {
    return t("gal_us_const");
  }
};
